import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { studentPointsQuery } from '@/api/progress';
import type { ApiError } from '@/api/client';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { humanizeError } from '@/lib/errors';
import { pointsRows } from '@/lib/studentPoints';

/**
 * Баллы ученика для сотрудника: сумма, строки «курс — баллы», «вне курсов».
 * Договор — `docs/CONTRACTS/student-points.md` §4. Стоит и в кураторской
 * карточке ученика, и в админской.
 *
 * ⚠️ ЗАЧЕМ ЭТА КАРТОЧКА ПОЯВИЛАСЬ. До 12.09.2026 куратор видел «0 БАЛЛ» у
 * ученика, который весь месяц работал: экран читал колонку
 * `users.student_profiles.points`, которую не пишет ни одна служба. На проде там
 * ноль у всех 2058 профилей, а нулём были показаны 772 ученика из 850 видимых
 * кураторам — при настоящих 155 551 балле. Настоящие баллы живут в журнале
 * progress, и ручки для сотрудника не существовало вовсе.
 *
 * ⚠️ ОШИБКА ПОКАЗЫВАЕТСЯ ОШИБКОЙ, А НЕ НУЛЁМ. Соблазн подставить ноль, «чтобы
 * экран не пустовал», воспроизвёл бы ровно тот дефект, который эта работа чинит:
 * сбой чтения нельзя отличить от ученика без баллов. Сервер поэтому и отвечает
 * 500, а не нулём.
 */
/**
 * `showTotal` — рисовать ли общую сумму плашкой в заголовке карточки.
 *
 * ⚠️ ЭТО НЕ УКРАШАТЕЛЬСТВО, А БОРЬБА С ТРОЙНЫМ ПОВТОРОМ. У куратора карточка
 * ученика показывает сумму уже дважды: плашкой у имени и плиткой «Баллы». Третье
 * «128 БАЛЛОВ» в заголовке разбивки — найдено на живом экране 12.09.2026 — не
 * добавляет ничего, а глаз спотыкается и ищет разницу между числами, которой
 * нет. В админской карточке ни плашки, ни плитки нет, и там сумму показать
 * больше негде — поэтому умолчание `true`.
 */
export function StudentPointsCard({ studentId, showTotal = true }: { studentId: string; showTotal?: boolean }) {
  const { t } = useTranslation();
  const points = useQuery(studentPointsQuery(studentId));

  const rows = pointsRows(points.data);
  // Клиент API отдаёт ошибку как `{status, message, raw}` (`normaliseError`), но
  // типом react-query это `Error` — приведение через `unknown`, как и в других
  // местах, читающих код ответа.
  const status = (points.error as unknown as ApiError | null)?.status ?? 0;

  return (
    <Card className="space-y-3" data-student-points>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>{t('studentPoints.title')}</CardTitle>
        {showTotal && points.data ? (
          <Pill variant="success" data-student-points-total>
            {t('curatorStudents.points', { count: points.data.total_points })}
          </Pill>
        ) : null}
      </div>

      {points.isLoading ? <p className="text-sm text-on-surface-variant">{t('common.loading')}</p> : null}

      {points.isError ? (
        <p className="text-sm text-error" role="alert" data-student-points-error>
          {status === 403 ? t('studentPoints.forbidden') : humanizeError(points.error)}
        </p>
      ) : null}

      {points.data && !rows.length ? (
        <p className="text-sm text-on-surface-variant">{t('studentPoints.empty')}</p>
      ) : null}

      {rows.length ? (
        <ul className="divide-y divide-outline-variant/40">
          {rows.map((row) =>
            row.kind === 'outside' ? (
              <li key={row.id} className="flex items-start justify-between gap-3 py-2" data-student-points-outside>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-on-surface">{t('studentPoints.outside')}</p>
                  <p className="text-xs text-on-surface-variant">{t('studentPoints.outsideHint')}</p>
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-on-surface">
                  {t('curatorStudents.points', { count: row.points })}
                </span>
              </li>
            ) : (
              <li key={row.id} className="flex items-start justify-between gap-3 py-2" data-student-points-course>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-on-surface">
                    {row.title || t('studentPoints.courseGone')}
                  </p>
                  {row.deleted ? (
                    <p className="text-xs text-on-surface-variant">{t('studentPoints.courseDeleted')}</p>
                  ) : null}
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-on-surface">
                  {t('curatorStudents.points', { count: row.points })}
                </span>
              </li>
            ),
          )}
        </ul>
      ) : null}
    </Card>
  );
}
